/* =====================================================================
 * AppManagePage — 앱 관리 (owner 전용)
 *
 * 실제 백엔드 API 연동: 사용자 목록 조회 · 검색 · 역할(admin ↔ user) 변경
 * 서버사이드 페이지네이션 (페이지당 10명)
 * ===================================================================== */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import { Calendar, ChevronDown, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Globe, Loader2, MessageSquare, RefreshCw, Zap } from 'lucide-react';
import { useChatStore } from '@auw/stores';
import { listUsers, updateUserRole, adminUpdateUserPlan, getDailyUsageStats, type AppUserItem, type DailyUsageStatsResponse } from '@auw/api-client';
import type { PlanType, UserRole } from '@auw/workspace-utils';
import { PLANS } from '@auw/workspace-utils';
import { Input } from '@auw/workspace-ui';
import { Badge } from '@auw/workspace-ui';
import { useFeatureFlags } from '@auw/feature-flags';

/* ------------------------------------------------------------------ */
/* Constants                                                           */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 10;
const ALL_PLANS: PlanType[] = ['free', 'pro', 'premium', 'max', 'business', 'enterprise'];

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */

export const AppManagePage: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const addToast = useChatStore((s) => s.addToast);
  /* P11-5j: granular admin/app gates. Role/plan dropdowns and the daily
   * usage panel each have their own flag — disabling them collapses
   * those columns / sections without removing the broader user table. */
  const appFlags = useFeatureFlags();
  const roleChangeEnabled = appFlags.isEnabled('admin.app.roleChange');
  const planChangeEnabled = appFlags.isEnabled('admin.app.planChange');
  const usageDashboardEnabled = appFlags.isEnabled('admin.app.usageDashboard');

  const [users, setUsers] = useState<AppUserItem[]>([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | UserRole>('all');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [planDropdown, setPlanDropdown] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [roleUpdating, setRoleUpdating] = useState<string | null>(null);
  const [planUpdating, setPlanUpdating] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  /* ---- 토큰 사용량 ---- */
  const [usageDate, setUsageDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
  });
  const [usageStats, setUsageStats] = useState<DailyUsageStatsResponse | null>(null);
  const [usageLoading, setUsageLoading] = useState(false);

  /* ---- 드롭다운 외부 클릭 닫기 ---- */
  useEffect(() => {
    if (!openDropdown && !planDropdown) return;
    const handler = () => { setOpenDropdown(null); setPlanDropdown(null); };
    const timer = setTimeout(() => document.addEventListener('click', handler), 0);
    return () => { clearTimeout(timer); document.removeEventListener('click', handler); };
  }, [openDropdown, planDropdown]);

  /* ---- 데이터 로드 ---- */
  const fetchUsers = useCallback(
    async (searchQuery?: string, role?: string, pageNum: number = 1) => {
      setLoading(true);
      try {
        const res = await listUsers({
          q: searchQuery || undefined,
          role: role && role !== 'all' ? role : undefined,
          skip: (pageNum - 1) * PAGE_SIZE,
          limit: PAGE_SIZE,
        });
        setUsers(res.users);
        setTotal(res.total);
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.usersLoadFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  /* 초기 로드 */
  useEffect(() => {
    fetchUsers('', 'all', 1);
  }, [fetchUsers]);

  /* ---- 토큰 사용량 로드 ---- */
  const fetchUsageStats = useCallback(
    async (date: string) => {
      setUsageLoading(true);
      try {
        const stats = await getDailyUsageStats(date);
        setUsageStats(stats);
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.usageStatsFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setUsageLoading(false);
      }
    },
    [addToast],
  );

  useEffect(() => {
    if (!usageDashboardEnabled) return;
    fetchUsageStats(usageDate);
  }, [fetchUsageStats, usageDate, usageDashboardEnabled]);

  /* ---- 검색 debounce (300ms) ---- */
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setPage(1);
        fetchUsers(value, roleFilter, 1);
      }, 300);
    },
    [fetchUsers, roleFilter],
  );

  /* ---- 역할 필터 변경 ---- */
  const handleRoleFilterChange = useCallback(
    (role: 'all' | UserRole) => {
      setRoleFilter(role);
      setPage(1);
      fetchUsers(query, role, 1);
    },
    [fetchUsers, query],
  );

  /* ---- 페이지 변경 ---- */
  const goToPage = useCallback(
    (p: number) => {
      const clamped = Math.max(1, Math.min(p, totalPages));
      setPage(clamped);
      fetchUsers(query, roleFilter, clamped);
    },
    [fetchUsers, query, roleFilter, totalPages],
  );

  /* ---- 역할 변경 ---- */
  const handleRoleChange = useCallback(
    async (userId: string, newRole: UserRole) => {
      setOpenDropdown(null);
      setRoleUpdating(userId);
      try {
        await updateUserRole(userId, newRole);
        // 현재 페이지 갱신
        await fetchUsers(query, roleFilter, page);
        const target = users.find((u) => u.user_id === userId);
        addToast({
          type: 'success',
          title: t('admin:appManage.roleChangeSuccess'),
          description: `${target?.name ?? target?.email ?? ''} → ${roleDisplayName(newRole as UserRole)}`,
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.roleChangeFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setRoleUpdating(null);
      }
    },
    [addToast, fetchUsers, query, roleFilter, page, users],
  );

  /* ---- 플랜 변경 ---- */
  const handlePlanChange = useCallback(
    async (userId: string, newPlan: PlanType) => {
      setPlanDropdown(null);
      setPlanUpdating(userId);
      try {
        await adminUpdateUserPlan(userId, newPlan);
        await fetchUsers(query, roleFilter, page);
        const target = users.find((u) => u.user_id === userId);
        addToast({
          type: 'success',
          title: t('admin:appManage.planChangeSuccess'),
          description: `${target?.name ?? target?.email ?? ''} → ${planDisplayName(newPlan)}`,
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: t('admin:appManage.planChangeFail'),
          description: err instanceof Error ? err.message : t('admin:appManage.unknownError'),
        });
      } finally {
        setPlanUpdating(null);
      }
    },
    [addToast, fetchUsers, query, roleFilter, page, users],
  );

  /* ---- 페이지 번호 배열 생성 (최대 7개 표시) ---- */
  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <Wrap>
      <Card>
        <CardTitle>{t('admin:appManage.title')}</CardTitle>
        <CardDesc>{t('admin:appManage.description')}</CardDesc>

        {/* Search + Filter */}
        <ToolBar>
          <SearchWrap>
            <Input
              variant="search"
              placeholder={t('admin:appManage.searchPlaceholder')}
              value={query}
              onChange={(e) => handleQueryChange(e.target.value)}
            />
          </SearchWrap>
          <FilterPill>
            {(['all', 'owner', 'admin', 'user'] as const).map((r) => (
              <FilterBtn key={r} $active={roleFilter === r} onClick={() => handleRoleFilterChange(r)}>
                {r === 'all' ? t('admin:appManage.all') : roleDisplayName(r)}
              </FilterBtn>
            ))}
          </FilterPill>
        </ToolBar>

        {/* Table */}
        <TableWrap>
          <Table>
            <thead>
              <Tr>
                <Th>{t('admin:appManage.userColumn')}</Th>
                <Th>{t('admin:appManage.emailColumn')}</Th>
                <Th>{t('admin:appManage.roleColumn')}</Th>
                {planChangeEnabled && <Th>{t('admin:appManage.planColumn')}</Th>}
                <Th>{t('admin:appManage.joinDateColumn')}</Th>
                {roleChangeEnabled && <Th style={{ width: 100 }}>{t('admin:appManage.manageColumn')}</Th>}
              </Tr>
            </thead>
            <tbody>
              {(() => {
                const colCount = 4 + (planChangeEnabled ? 1 : 0) + (roleChangeEnabled ? 1 : 0);
                if (loading && users.length === 0) {
                  return (
                    <Tr>
                      <Td colSpan={colCount}>
                        <EmptyRow><Loader2 size={16} className="spin" /> {t('admin:appManage.loading')}</EmptyRow>
                      </Td>
                    </Tr>
                  );
                }
                if (users.length === 0) {
                  return (
                    <Tr>
                      <Td colSpan={colCount}>
                        <EmptyRow>{t('admin:appManage.noResults')}</EmptyRow>
                      </Td>
                    </Tr>
                  );
                }
                return users.map((u) => (
                  <Tr key={u.user_id}>
                    <Td>
                      <UserCell>
                        <UserAvatar>{avatarLetter(u.name, u.email)}</UserAvatar>
                        <UserName>{u.name || '—'}</UserName>
                      </UserCell>
                    </Td>
                    <Td><EmailText>{u.email || '—'}</EmailText></Td>
                    <Td>
                      <Badge variant={u.role === 'admin' || u.role === 'owner' ? 'info' : 'default'}>
                        {u.role === 'admin' || u.role === 'owner' ? t('admin:appManage.adminBadge') : t('admin:appManage.memberBadge')}
                      </Badge>
                    </Td>
                    {planChangeEnabled && (
                      <Td>
                        {planUpdating === u.user_id ? (
                          <DisabledText><Loader2 size={14} className="spin" /></DisabledText>
                        ) : (
                          <PlanDropdown>
                            <PlanBadgeBtn
                              $plan={u.plan || 'free'}
                              onClick={() => setPlanDropdown(planDropdown === u.user_id ? null : u.user_id)}
                            >
                              {planDisplayName(u.plan || 'free')} <ChevronDown size={10} />
                            </PlanBadgeBtn>
                            {planDropdown === u.user_id && (
                              <RoleDropdownMenu>
                                {ALL_PLANS.filter((p) => p !== (u.plan || 'free')).map((p) => (
                                  <RoleDropdownItem key={p} onClick={() => handlePlanChange(u.user_id, p)}>
                                    {planDisplayName(p)}
                                  </RoleDropdownItem>
                                ))}
                              </RoleDropdownMenu>
                            )}
                          </PlanDropdown>
                        )}
                      </Td>
                    )}
                    <Td><DateText>{formatDate(u.created_at)}</DateText></Td>
                    {roleChangeEnabled && (
                      <Td>
                        {u.role === 'owner' ? (
                          <DisabledText>—</DisabledText>
                        ) : roleUpdating === u.user_id ? (
                          <DisabledText><Loader2 size={14} className="spin" /></DisabledText>
                        ) : (
                          <RoleDropdown>
                            <RoleDropdownBtn
                              onClick={() => setOpenDropdown(openDropdown === u.user_id ? null : u.user_id)}
                            >
                              {t('admin:appManage.changeBtn')} <ChevronDown size={12} />
                            </RoleDropdownBtn>
                            {openDropdown === u.user_id && (
                              <RoleDropdownMenu>
                                {u.role !== 'admin' && (
                                  <RoleDropdownItem onClick={() => handleRoleChange(u.user_id, 'admin')}>
                                    {t('admin:appManage.promoteAdmin')}
                                  </RoleDropdownItem>
                                )}
                                {u.role !== 'user' && (
                                  <RoleDropdownItem onClick={() => handleRoleChange(u.user_id, 'user')}>
                                    {t('admin:appManage.demoteUser')}
                                  </RoleDropdownItem>
                                )}
                              </RoleDropdownMenu>
                            )}
                          </RoleDropdown>
                        )}
                      </Td>
                    )}
                  </Tr>
                ));
              })()}
            </tbody>
          </Table>
        </TableWrap>

        {/* Footer: count + pagination */}
        <TableFooter>
          <CountLabel>
            {total === 0
              ? t('admin:appManage.countEmpty')
              : t('admin:appManage.countLabel', { start: (page - 1) * PAGE_SIZE + 1, end: Math.min(page * PAGE_SIZE, total), total })}
          </CountLabel>

          {totalPages > 1 && (
            <Pagination>
              <PageBtn disabled={page === 1} onClick={() => goToPage(1)} aria-label={t('admin:appManage.firstPage')}>
                <ChevronsLeft size={14} />
              </PageBtn>
              <PageBtn disabled={page === 1} onClick={() => goToPage(page - 1)} aria-label={t('admin:appManage.prevPage')}>
                <ChevronLeft size={14} />
              </PageBtn>

              {pageNumbers.map((p, i) =>
                p === '...' ? (
                  <PageEllipsis key={`e${i}`}>…</PageEllipsis>
                ) : (
                  <PageBtn
                    key={p}
                    $active={p === page}
                    onClick={() => goToPage(p as number)}
                  >
                    {p}
                  </PageBtn>
                ),
              )}

              <PageBtn disabled={page === totalPages} onClick={() => goToPage(page + 1)} aria-label={t('admin:appManage.nextPage')}>
                <ChevronRight size={14} />
              </PageBtn>
              <PageBtn disabled={page === totalPages} onClick={() => goToPage(totalPages)} aria-label={t('admin:appManage.lastPage')}>
                <ChevronsRight size={14} />
              </PageBtn>
            </Pagination>
          )}
        </TableFooter>
      </Card>

      {/* ---- 토큰 사용량 ---- */}
      {usageDashboardEnabled && (
      <Card style={{ marginTop: 24 }}>
        <UsageTitleRow>
          <UsageTitleLeft>
            <CardTitle>{t('admin:appManage.tokenUsage')}</CardTitle>
            <RefreshBtn
              onClick={() => fetchUsageStats(usageDate)}
              disabled={usageLoading}
              aria-label={t('admin:appManage.refresh')}
            >
              <RefreshCw size={14} className={usageLoading ? 'spin' : ''} />
            </RefreshBtn>
          </UsageTitleLeft>
          <DatePickerWrap>
            <Calendar size={14} />
            <DateInput
              type="date"
              value={usageDate}
              max={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setUsageDate(e.target.value)}
            />
          </DatePickerWrap>
        </UsageTitleRow>
        <CardDesc>{t('admin:appManage.usageUpdateNote')}</CardDesc>

        {usageLoading ? (
          <UsageLoadingWrap><Loader2 size={18} className="spin" /> {t('admin:appManage.usageLoading')}</UsageLoadingWrap>
        ) : usageStats ? (
          <StatsGrid>
            <StatCard>
              <StatIconWrap $color="#3b82f6"><Zap size={18} /></StatIconWrap>
              <StatContent>
                <StatLabel>{t('admin:appManage.dailyTokenUsage')}</StatLabel>
                <StatValue>{formatTokens(usageStats.total_tokens)}</StatValue>
                <StatSub>{t('admin:appManage.totalUserSum')}</StatSub>
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconWrap $color="#10b981"><Globe size={18} /></StatIconWrap>
              <StatContent>
                <StatLabel>{t('admin:appManage.countryTokenUsage')}</StatLabel>
                {usageStats.by_country.length === 0 ? (
                  <StatSub>{t('admin:appManage.noData')}</StatSub>
                ) : (
                  <CountryList>
                    {usageStats.by_country.slice(0, 5).map((c) => (
                      <CountryRow key={c.country}>
                        <CountryName>{t(`admin:appManage.countries.${c.country}`, c.country)}</CountryName>
                        <CountryTokens>{formatTokens(c.tokens)}</CountryTokens>
                        <CountrySub>{t('admin:appManage.membersUnit', { count: c.user_count })}</CountrySub>
                      </CountryRow>
                    ))}
                    {usageStats.by_country.length > 5 && (
                      <StatSub>{t('admin:appManage.moreCountries', { count: usageStats.by_country.length - 5 })}</StatSub>
                    )}
                  </CountryList>
                )}
              </StatContent>
            </StatCard>

            <StatCard>
              <StatIconWrap $color="#8b5cf6"><MessageSquare size={18} /></StatIconWrap>
              <StatContent>
                <StatLabel>{t('admin:appManage.chatEvents')}</StatLabel>
                <StatValue>{usageStats.chat_events.toLocaleString()}</StatValue>
                <StatSub>{t('admin:appManage.eventUnit')}</StatSub>
              </StatContent>
            </StatCard>
          </StatsGrid>
        ) : (
          <UsageLoadingWrap>{t('admin:appManage.selectDate')}</UsageLoadingWrap>
        )}
      </Card>
      )}
    </Wrap>
  );
};

export default AppManagePage;

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function roleDisplayName(role: UserRole | string): string {
  switch (role) {
    case 'owner': return 'Owner';
    case 'admin': return 'Admin';
    case 'user': return 'User';
    default: return role;
  }
}

function planDisplayName(plan: string): string {
  const info = PLANS[plan as PlanType];
  return info ? info.name : plan;
}

function formatTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}


function avatarLetter(name: string | null, email: string | null): string {
  const src = (name || email || '?').trim();
  const ch = src.charAt(0);
  return /[\u3131-\uD79D]/.test(ch) ? ch : ch.toUpperCase();
}

function formatDate(raw: string | null): string {
  if (!raw) return '—';
  try {
    return raw.slice(0, 10);
  } catch {
    return raw;
  }
}

/** 표시할 페이지 번호 배열 생성 (최대 7개, 줄임표 포함) */
function buildPageNumbers(current: number, total: number): (number | '...')[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [];

  if (current <= 4) {
    // 앞쪽: 1 2 3 4 5 ... last
    for (let i = 1; i <= 5; i++) pages.push(i);
    pages.push('...');
    pages.push(total);
  } else if (current >= total - 3) {
    // 뒷쪽: 1 ... n-4 n-3 n-2 n-1 n
    pages.push(1);
    pages.push('...');
    for (let i = total - 4; i <= total; i++) pages.push(i);
  } else {
    // 중간: 1 ... cur-1 cur cur+1 ... last
    pages.push(1);
    pages.push('...');
    pages.push(current - 1);
    pages.push(current);
    pages.push(current + 1);
    pages.push('...');
    pages.push(total);
  }

  return pages;
}

/* ------------------------------------------------------------------ */
/* Styles                                                              */
/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  flex: 1;
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 32px 24px 80px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }

  .spin {
    animation: spin 1s linear infinite;
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const Card = styled.main`
  width: 100%;
  max-width: 900px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  padding: 36px 36px 32px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 24px 16px 20px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  }
`;

const CardTitle = styled.h1`
  margin: 0 0 4px;
  font-size: 1.375rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const CardDesc = styled.p`
  margin: 0 0 24px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

/* ---- Toolbar ---- */

const ToolBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SearchWrap = styled.div`
  flex: 1;
  min-width: 200px;
`;


const FilterPill = styled.div`
  display: inline-flex;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  overflow: hidden;
  flex-shrink: 0;
`;

const FilterBtn = styled.button<{ $active: boolean }>`
  padding: 7px 14px;
  border: none;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover { background: ${theme.colors.bgHover}; }
        `}
`;

/* ---- Table ---- */

const TableWrap = styled.div`
  overflow: visible;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 560px;
`;

const Tr = styled.tr`
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  &:last-child { border-bottom: none; }
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 14px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)'};
`;

const Td = styled.td`
  padding: 10px 14px;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.textPrimary};
  vertical-align: middle;
`;

const EmptyRow = styled.div`
  text-align: center;
  padding: 32px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

/* ---- User cell ---- */

const UserCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const UserAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${({ theme }) => (theme.mode === 'dark' ? '#3a3a3a' : '#e8e5e1')};
  color: ${({ theme }) => (theme.mode === 'dark' ? '#d4d0cc' : '#5c5550')};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
`;

const UserName = styled.span`
  font-weight: 500;
  white-space: nowrap;
`;

const EmailText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.8125rem;
`;

const DateText = styled.span`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  font-variant-numeric: tabular-nums;
`;

const DisabledText = styled.span`
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
  display: inline-flex;
  align-items: center;
`;

/* ---- Plan badge dropdown ---- */

const PlanDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const PLAN_COLORS: Record<string, { bg: string; color: string }> = {
  free: { bg: 'rgba(128,128,128,0.12)', color: '#888' },
  pro: { bg: 'rgba(59,130,246,0.12)', color: '#3b82f6' },
  premium: { bg: 'rgba(139,92,246,0.12)', color: '#8b5cf6' },
  max: { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  business: { bg: 'rgba(16,185,129,0.12)', color: '#10b981' },
  enterprise: { bg: 'rgba(236,72,153,0.12)', color: '#ec4899' },
};

const PlanBadgeBtn = styled.button<{ $plan: string }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: none;
  border-radius: 12px;
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  background: ${({ $plan }) => PLAN_COLORS[$plan]?.bg ?? PLAN_COLORS.free.bg};
  color: ${({ $plan }) => PLAN_COLORS[$plan]?.color ?? PLAN_COLORS.free.color};
  &:hover { opacity: 0.8; }
`;

/* ---- Role dropdown ---- */

const RoleDropdown = styled.div`
  position: relative;
  display: inline-block;
`;

const RoleDropdownBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border: none;
  border-radius: 12px;
  background: rgba(128,128,128,0.12);
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.6875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover { opacity: 0.8; }
`;

const RoleDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 20;
  min-width: 150px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 4px;
  animation: fadeIn 0.12s ease;

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(-4px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const RoleDropdownItem = styled.button`
  width: 100%;
  text-align: left;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};
  &:hover { background: ${({ theme }) => theme.colors.bgHover}; }
`;

/* ---- Footer + Pagination ---- */

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  flex-wrap: wrap;
  gap: 12px;
`;

const CountLabel = styled.div`
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Pagination = styled.nav`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const PageBtn = styled.button<{ $active?: boolean; disabled?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  padding: 0 6px;
  border: 1px solid transparent;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  font-size: 0.75rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  ${({ $active, theme }) =>
    $active
      ? css`
          background: ${theme.colors.textPrimary};
          color: ${theme.colors.bgPrimary};
          border-color: ${theme.colors.textPrimary};
          pointer-events: none;
        `
      : css`
          background: transparent;
          color: ${theme.colors.textSecondary};
          &:hover:not(:disabled) {
            background: ${theme.colors.bgHover};
            border-color: ${theme.colors.border};
          }
        `}

  &:disabled {
    opacity: 0.35;
    cursor: default;
    pointer-events: none;
  }
`;

const PageEllipsis = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textMuted};
  user-select: none;
`;

/* ---- Token Usage Section ---- */

const UsageTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 4px;
`;

const UsageTitleLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RefreshBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: ${({ theme }) => theme.colors.textMuted};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgHover};
    color: ${({ theme }) => theme.colors.textPrimary};
  }
  &:disabled { opacity: 0.4; cursor: default; }
`;

const DatePickerWrap = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  transition: border-color ${({ theme }) => theme.transitions.fast};
  &:hover { border-color: ${({ theme }) => theme.colors.borderInput}; }
`;

const DateInput = styled.input`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: 0.8125rem;
  font-weight: 500;
  outline: none;
  cursor: pointer;
  font-family: inherit;

  &::-webkit-calendar-picker-indicator {
    opacity: 0;
    position: absolute;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
`;

const UsageLoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;
  color: ${({ theme }) => theme.colors.textMuted};
  font-size: 0.8125rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-top: 16px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  display: flex;
  gap: 14px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) =>
    theme.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)'};
`;

const StatIconWrap = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${({ $color }) => $color}18;
  color: ${({ $color }) => $color};
`;

const StatContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const StatLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const StatValue = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
  line-height: 1.2;
  font-variant-numeric: tabular-nums;
`;

const StatSub = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const CountryList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 4px;
`;

const CountryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.75rem;
`;

const CountryName = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  min-width: 48px;
`;

const CountryTokens = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  font-variant-numeric: tabular-nums;
`;

const CountrySub = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
`;
